/* oxlint-disable prefer-const */
// We have a pattern to use `let` in these files. Don't mess with it.

import { organizationSchema } from "@medoma/models/organizations";
import type { SiteId } from "@medoma/models/sites";
import { UserManager, type UserManagerSettings } from "oidc-client-ts";

let logisticsApiUrl =
  import.meta.env.DEV && import.meta.env.VITE_LOCAL_LOGISTICS_BACKEND
    ? () => `https://localhost:7240`
    : (organization: string) =>
        `https://logistics-${organization}-dev.medoma.com`;
let logisticsSseUrl =
  import.meta.env.DEV && import.meta.env.VITE_LOCAL_LOGISTICS_BACKEND
    ? () => `https://localhost:7240`
    : (organization: string) =>
        `https://logisticsbg-${organization}-dev.medoma.com`;
let devicesUrl =
  import.meta.env.DEV && import.meta.env.VITE_LOCAL_DEVICES_BACKEND
    ? () => `https://localhost:7240/device`
    : (organization: string, siteId: SiteId) =>
        `https://devices-${organization}-dev.medoma.com/${siteId}`;
let mapboxApiUrl = `https://api.mapbox.com`;
let mapboxPublicAccessToken = `pk.eyJ1IjoibWVkb21hIiwiYSI6ImNsN2Z6c2J0bTAwOHozd25xM3JlNzZ6bjUifQ.v2XsEAYwGo00Vk0XZteAZQ`;
const intercomAppId = `cqn68r2g`;
let patientApiUrl =
  import.meta.env.DEV && import.meta.env.VITE_LOCAL_PATIENTS_BACKEND
    ? () => `https://localhost:7023`
    : (organization: string) => `https://pas-${organization}-dev.medoma.com`;
let authApiUrl =
  import.meta.env.DEV && import.meta.env.VITE_LOCAL_AUTH_BACKEND
    ? `https://localhost:5001`
    : `https://auth-dev.medoma.com`;
let notificationApiUrl =
  import.meta.env.DEV && import.meta.env.VITE_LOCAL_NOTIFICATION_BACKEND
    ? () => `https://localhost:7240/notification`
    : (organization: string) =>
        `https://logistics-${organization}-dev.medoma.com/notification`;
let environment = import.meta.env.DEV ? `local` : `dev`;

const hostname = window.location.hostname;

// Inspired by https://daveceddia.com/multiple-environments-with-react/
if (hostname.endsWith(`app.medoma.com`)) {
  logisticsApiUrl = (organization: string) =>
    `https://logistics-${organization}.medoma.com`;
  logisticsSseUrl = (organization: string) =>
    `https://logisticsbg-${organization}.medoma.com`;
  patientApiUrl = (organization: string) =>
    `https://pas-${organization}.medoma.com`;
  authApiUrl = `https://auth.medoma.com`;
  notificationApiUrl = (organization: string) =>
    `https://logistics-${organization}.medoma.com/notification`;
  environment = `prod`;
  devicesUrl = (organization: string, siteId: SiteId) =>
    `https://devices-${organization}.medoma.com/${siteId}`;
} else if (hostname.endsWith(`app.test.medoma.com`)) {
  logisticsApiUrl = (organization: string) =>
    `https://logistics-${organization}-test.medoma.com`;
  logisticsSseUrl = (organization: string) =>
    `https://logisticsbg-${organization}-test.medoma.com`;
  patientApiUrl = (organization: string) =>
    `https://pas-${organization}-test.medoma.com`;
  authApiUrl = `https://auth-test.medoma.com`;
  notificationApiUrl = (organization: string) =>
    `https://logistics-${organization}-test.medoma.com/notification`;
  environment = `test`;
  devicesUrl = (organization: string, siteId: SiteId) =>
    `https://devices-${organization}-test.medoma.com/${siteId}`;
}

export const scopes =
  "openid IdentityServerApi create.otp revoke_session set_temporary_pin organization logistic.api patient.api employee_profile";
const oidcConfig: UserManagerSettings = {
  authority: authApiUrl,
  client_id: "medoma-center",
  redirect_uri: `${window.location.origin}/login`,
  scope: scopes,
};
export const getAcrValues = () => {
  // Local flow. Adjust as needed for local development.
  if (window.location.hostname === "localhost") {
    return "idp:siths";
  }

  // Legacy flow. Works for every client defaulting to SITHS.
  if (window.location.hostname.startsWith("app.")) {
    return "idp:siths";
  }

  // New flow. URL should be {organization}.app.medoma.com
  const organizationPositionInURL = window.location.hostname.split(".")[0];
  // Can throw if bad URL is provided.
  const knownOrganization = organizationSchema.parse(organizationPositionInURL);
  return `tenant:${knownOrganization}`;
};

export const userManager = new UserManager(oidcConfig);

export const {
  getLogisticsApiUrl,
  getLogisticsSseUrl,
  getPatientApiUrl,
  getNotificationApiUrl,
  getDevicesUrl,
  MAPBOX_API_URL,
  MAPBOX_PUBLIC_ACCESS_TOKEN,
  AUTH_API_URL,
  ENVIRONMENT,
  CHAT_ENVIRONMENT,
  INTERCOM_APP_ID,
} = {
  getLogisticsApiUrl: (organization: string) => logisticsApiUrl(organization),
  getLogisticsSseUrl: (organization: string) => logisticsSseUrl(organization),
  getPatientApiUrl: (organization: string) => patientApiUrl(organization),
  getNotificationApiUrl: (organization: string) =>
    notificationApiUrl(organization),
  getDevicesUrl: (organization: string, siteId: SiteId) =>
    devicesUrl(organization, siteId),
  MAPBOX_API_URL: mapboxApiUrl,
  MAPBOX_PUBLIC_ACCESS_TOKEN: mapboxPublicAccessToken,
  AUTH_API_URL: authApiUrl,
  ENVIRONMENT: environment,
  CHAT_ENVIRONMENT: environment === "local" ? "dev" : environment,
  INTERCOM_APP_ID: intercomAppId,
};
